import Component from '@glimmer/component';
import { tracked, cached } from '@glimmer/tracking';
import { action } from '@ember/object';
import { assert } from '@ember/debug';
import { isNone } from '@ember/utils';
import type Owner from '@ember/owner';

import { computeGeometry, tickPositions } from '../utils/graphUtils.ts';
import type {
  GraphCoordinate,
  GraphOptions,
  GraphGeometry,
  MultilineGraphModel,
  CircleCoordinate,
  HoverTextValues,
  HoverTitles,
} from '../index.d.ts';
import { getClosestDataX, getXValues, getYValues, getCircleCoordinates } from '../utils/hoverUtils.ts';
import functionLine from '../helpers/function-line.ts';
import onMoveOutside from '../modifiers/on-move-outside.ts';
import mathAdd from '../helpers/math-add.ts';

interface AditoolsMultilineGraphArgs {
  model: MultilineGraphModel;
  options: GraphOptions;
  xTitleKey: string;
  yTitleKey: string;
  y2TitleKey: string;
  lineClasses: string[];
  width?: number;
  height?: number;
  leftMargin?: number;
  topMargin?: number;
  rightMargin?: number;
  bottomMargin?: number;
  leftLabelOffset?: number;
  rightLabelOffset?: number;
  bottomLabelOffset?: number;
  onHighlightedLinesChanged?: (highlightedLines: number[]) => void;
}

interface AditoolsMultilineGraphSignature {
  Args: AditoolsMultilineGraphArgs;
  Element: SVGSVGElement;
  Blocks: { default: [GraphGeometry, GraphGeometry?] };
}
export default class AditoolsMultilineGraph extends Component<AditoolsMultilineGraphSignature> {
  @tracked hoverClientX = 0;
  @tracked hoverClientY = 0;
  @tracked hoverReady = false;
  @tracked prevHighlightedLines: number[] = [];
  hoverBox: [number, number] = [80, 80];
  functionLine = functionLine;
  mathAdd = mathAdd;
  onMoveOutside = onMoveOutside;

  // On initialization, verify required parameters have usable structure.

  constructor(owner: Owner, args: AditoolsMultilineGraphArgs) {
    super(owner, args);

    assert('`model` is required', !isNone(this.args.model));
    assert('`options` is required', !isNone(this.args.options));
    assert('`xTitleKey` is required', !isNone(this.args.xTitleKey));
    assert('`yTitleKey` is required', !isNone(this.args.yTitleKey));
    assert('`model` requires a limits member', !isNone(this.args.model) && !isNone(this.args.model.limits));

    if (!isNone(this.args.model) && !isNone(this.args.model.limits)) {
      const limits = this.args.model.limits;
      assert(`model.limits.xmin contains non-numerical value ${limits.xmin}`, !isNaN(limits.xmin));
      assert(`model.limits.xmax contains non-numerical value ${limits.xmax}`, !isNaN(limits.xmax));
      assert(`model.limits.ymin contains non-numerical value ${limits.ymin}`, !isNaN(limits.ymin));
      assert(`model.limits.ymax contains non-numerical value ${limits.ymax}`, !isNaN(limits.ymax));
    }
  }

  // Optional parameters with defaults applied
  get leftMargin(): number {
    return this.args.leftMargin ?? 70;
  }
  get rightMargin(): number {
    return this.args.rightMargin ?? 5;
  }
  get topMargin(): number {
    return this.args.topMargin ?? 15;
  }
  get bottomMargin(): number {
    return this.args.bottomMargin ?? 65;
  }
  get leftLabelOffset(): number {
    return this.args.leftLabelOffset ?? 55;
  }
  get rightLabelOffset(): number {
    return this.args.rightLabelOffset ?? 70;
  }
  get bottomLabelOffset(): number {
    return this.args.bottomLabelOffset ?? 55;
  }

  get hoverTitles(): HoverTitles {
    if (this.args.options.hoverTitles) {
      return this.args.options.hoverTitles;
    } else {
      const graphs = this.args.model.data.length;
      return { headerLabel: '', lineLabels: Array(graphs).fill('') };
    }
  }

  @cached
  get lineClasses(): string[] {
    const model = this.args.model;
    const properLength = model.data.length + (model.dataRight ? model.dataRight.length : 0);
    if (this.args.lineClasses && this.args.lineClasses.length === properLength) {
      return this.args.lineClasses;
    } else {
      const realClasses = [];
      for (let index = 0; index < properLength; index++) {
        if (this.args.lineClasses && index < this.args.lineClasses.length && this.args.lineClasses[index]) {
          realClasses.push(this.args.lineClasses[index] as string); //Covered by guard clause above
        } else {
          realClasses.push('default-line-class');
        }
      }
      return realClasses;
    }
  }

  get width(): number {
    const defaultValue = 672;
    return typeof this.args.width === 'undefined' ? defaultValue : this.args.width;
  }

  get height(): number {
    const defaultValue = 485;
    return typeof this.args.height === 'undefined' ? defaultValue : this.args.height;
  }

  get chartWidth(): number {
    const rightOffset = this.hasSecondaryYAxis
      ? this.rightLabelOffset
      : this.hasRightOffset
        ? this.rightLabelOffset
        : this.args.options.hover
          ? 25
          : 0;
    let value = this.width - (this.leftMargin + this.rightMargin + rightOffset);
    if (value < 0) value = 0;
    return value;
  }

  get chartHeight(): number {
    let value = this.height - (this.topMargin + this.bottomMargin);
    if (value < 0) {
      value = 0;
    }
    return value;
  }

  get xGridLineEnd(): number {
    return this.args.options.xticksline === undefined
      ? 0
      : this.args.options.xticksline == 0
        ? this.chartHeight
        : <number>this.args.options.xticksline > this.chartHeight
          ? 0
          : this.chartHeight - <number>this.args.options.xticksline;
  }

  get yGridLineEnd(): number {
    return this.args.options.yticksline === undefined
      ? this.chartWidth
      : this.args.options.yticksline == 0
        ? 0
        : <number>this.args.options.yticksline > this.chartWidth
          ? this.chartWidth
          : this.args.options.yticksline;
  }

  get y2GridLineEnd(): number {
    return this.args.options.yticksline2 === undefined
      ? 0
      : this.args.options.yticksline2 == 0
        ? this.chartWidth
        : this.chartWidth - <number>this.args.options.yticksline2;
  }

  get hasRightOffset(): boolean {
    return this.args.options.yAxes === 2 ? false : this.args.options.rightMargin === 'wide' ? true : false;
  }

  get geometry(): GraphGeometry {
    return computeGeometry(this.args.model.limits, this.args.options, this.chartWidth, this.chartHeight);
  }

  get geometryRight(): GraphGeometry {
    const options = {
      xscale: this.args.options.xscale,
      yscale: this.args.options.yscale2 || 'linear',
      xticks: this.args.options.xticks,
      yticks: this.args.options.yticks2 || 5,
      yformat: this.args.options.yformat2 || this.args.options.yformat,
    };
    return this.args.model.limitsRight
      ? computeGeometry(this.args.model.limitsRight, options, this.chartWidth, this.chartHeight)
      : this.geometry;
  }

  get hasSecondaryYAxis(): boolean {
    if (this.args.options.yAxes === 2) {
      if (!this.args.model.dataRight || !this.args.model.limitsRight) {
        console.debug(
          'When yAxes is 2, model.dataRight and model.limitsRight must be supplied. Only left Y axis will be displayed.',
        );
      }
    } else if (typeof this.args.options.yAxes !== 'undefined' && this.args.options.yAxes !== 1) {
      console.debug('If supplied, options.yAxes must be 1 or 2. Only left Y axis will be displayed.');
    }
    return !!this.args.options.yAxes && this.args.options.yAxes === 2;
  }

  get xTitleCenter(): GraphCoordinate {
    return {
      x: this.chartWidth / 2,
      y: this.chartHeight + this.bottomLabelOffset,
    };
  }

  get yTitleCenter(): GraphCoordinate {
    return { x: -this.leftLabelOffset, y: this.chartHeight / 2 };
  }

  get yTitleCenter2(): GraphCoordinate {
    const calculatedX = this.width - (this.leftMargin + this.rightMargin);
    return { x: calculatedX, y: this.chartHeight / 2 };
  }

  get xTicks(): { value: string; x: number; y: number }[] {
    const count = this.geometry.tickcount[0] instanceof Array ? 5 : this.geometry.tickcount[0];
    return tickPositions(
      this.args.model.limits ? this.args.model.limits.xmin : 0,
      this.args.model.limits ? this.args.model.limits.xmax : 0,
      this.args.options.xticks || 5,
      this.args.options.xscale,
      this.args.options.xformat,
      this.geometry.xScale,
      count,
    ).map((item) => {
      return { value: item.value, x: item.coordinate, y: this.geometry.height };
    });
  }

  get yTicks(): { value: string; x: number; y: number }[] {
    const count = this.geometry.tickcount[1] instanceof Array ? 5 : this.geometry.tickcount[1];
    return tickPositions(
      this.args.model.limits ? this.args.model.limits.ymin : 0,
      this.args.model.limits ? this.args.model.limits.ymax : 0,
      this.args.options.yticks || 5,
      this.args.options.yscale,
      this.args.options.yformat,
      this.geometry.yScale,
      count,
    ).map((item) => {
      return { value: item.value, x: 0, y: item.coordinate };
    });
  }

  get yTicks2(): { value: string; x: number; y: number }[] {
    const calculatedX = this.width - (this.hasSecondaryYAxis ? this.rightLabelOffset + 20 : 0);
    const count = this.geometryRight.tickcount[1] instanceof Array ? 5 : this.geometryRight.tickcount[1];
    return tickPositions(
      this.args.model.limitsRight ? this.args.model.limitsRight.ymin : 0,
      this.args.model.limitsRight ? this.args.model.limitsRight.ymax : 0,
      this.args.options.yticks2 || 5,
      this.args.options.yscale2 || 'linear',
      this.args.options.yformat2 || this.args.options.yformat,
      this.geometryRight.yScale,
      count,
    ).map((item) => {
      return { value: item.value, x: calculatedX, y: item.coordinate };
    });
  }

  get hoverPosition(): { x: number; y: number } {
    return {
      x: Math.max(0, this.hoverClientX - this.hoverBox[0] / 2) - 30,
      y: Math.max(0, this.hoverClientY - this.hoverBox[1]),
    };
  }

  @cached
  get closestXValue(): number {
    return getClosestDataX(
      this.hoverClientX - this.leftMargin,
      this.args.model.data,
      this.geometry,
      this.args.model.dataRight,
    );
  }

  get circleCoordinates(): CircleCoordinate[] {
    return getCircleCoordinates(
      this.closestXValue,
      this.args.options,
      this.geometry,
      this.args.model.data,
      this.args.model.dataRight,
      this.geometryRight,
    );
  }

  get hoverText(): HoverTextValues {
    const { formattedValue, formattedValueWithUnit } = getXValues(this.closestXValue, this.args.options);
    const yValues = getYValues(
      this.closestXValue,
      this.args.options,
      [this.geometry.xScale(this.closestXValue), this.hoverClientY - this.topMargin],
      this.args.model.data,
      this.geometry,
      this.args.model.dataRight,
      this.geometryRight,
    );
    return {
      xAxisValueWithUnit: formattedValueWithUnit,
      xAxisValue: formattedValue,
      yAxisValues: yValues,
    };
  }

  get highlightedLines(): number[] {
    if (this.hoverText.yAxisValues.find((y) => y.focused == true)) {
      const indexLines: number[] = this.hoverText.yAxisValues
        .filter((y) => y.focused == true)
        .map((y) => y.index)
        .sort(function (a, b) {
          return a - b;
        });
      return indexLines;
    }
    return [];
  }

  @action updateHover(event: MouseEvent): void {
    if (this.args.options.hover) {
      const target = <HTMLInputElement>event.target;
      if (event.target && target.parentElement) {
        this.hoverClientX = event.offsetX;
        this.hoverClientY = event.offsetY;
        this.hoverReady = true;

        if (this.args.onHighlightedLinesChanged) {
          const a = performance.now();
          if (this.prevHighlightedLines.length !== this.highlightedLines.length) {
            this.args.onHighlightedLinesChanged(this.highlightedLines);
            this.prevHighlightedLines = [...this.highlightedLines];

            const b = performance.now();
            if (b - a > 20) {
              console.log(`Long execution time: It took ${b - a} ms to highlight line changes`);
            }
          } else {
            const containsAll = this.prevHighlightedLines.every((element) => {
              return this.highlightedLines?.indexOf(element) !== -1;
            });
            if (!containsAll) {
              this.args.onHighlightedLinesChanged(this.highlightedLines);
              this.prevHighlightedLines = [...this.highlightedLines];

              const b = performance.now();
              if (b - a > 20) {
                console.log(`Long execution time: It took ${b - a} ms to highlight line changes`);
              }
            }
          }
        }
      } else {
        console.log('mousemove: No target or no parent');
      }
    }
  }

  @action endHover(target: Element): void {
    let isSummary = false;
    for (let i = 0; i < target.classList.length; i++) {
      if (target.classList[i] === 'graph-hover_summary-line') {
        isSummary = true;
        break;
      }
    }
    if (this.hoverReady && !isSummary) {
      this.hoverReady = false;
    }
  }

  get showHover(): boolean {
    return !!this.hoverReady;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    AditoolsMultilineGraph: typeof AditoolsMultilineGraph;
  }
}
